import { sum } from 'lib/array/arrayUtils'
import { isFlightItem } from '../../../../checkout/lib/utils/flights/cart'
import { convertTimeObjectToMinutes, getJourneyV2IdKey } from 'lib/flights/flightUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { TrackableProduct } from 'components/OfferList/OfferListEventsContext'
import { isJourneyV2 } from 'lib/offer/offerTypes'
import { getCode } from 'country-list'
import { FlightsFareTypes } from 'constants/flight'
import { getFlightDetailsWithDedup } from 'api/order'

/**
 * Find a journey in the searchV2Flights state by searchId and journeyId.
 * This is used as a fallback when journeysById doesn't have the journey data,
 * which can happen for non-logged-in users.
 */
function findJourneyInSearchResults(
  state: App.State,
  searchId: string,
  journeyId: string
): App.JourneyV2 | undefined {
  const searchV2Flights = state.flights.searchV2Flights

  for (const key in searchV2Flights) {
    const searchResult = searchV2Flights[key]
    if (searchResult && searchResult.searchId === searchId && searchResult.fares) {
      // Each array represents a leg (e.g., outbound, return), and contains journey options
      for (const legFares of searchResult.fares) {
        for (const journey of legFares) {
          // Match by journey.id which corresponds to the journeyId stored in the cart item
          if (journey.id === journeyId) {
            return journey
          }
        }
      }
    }
  }

  return undefined
}

export function getFlightContextsForCheckout(state: App.State): Promise<Array<{
  data: {
    [x: string]: unknown;
  };
  schema: string;
}>> {
  const flightItems = state.checkout.cart.items.filter(item => isFlightItem(item))
  if (!flightItems.length) return Promise.resolve([])

  const flightItineraryContext = getFlightContextsForCheckoutItems(flightItems, state)
  return Promise.resolve(flightItineraryContext)
}

export function getFlightContextsForCheckoutItems(
  items: Array<App.Checkout.AnyItem>,
  state: App.State,
  journeys?: Array<App.JourneyV2>
): Array<{
  data: {
    [x: string]: unknown;
  };
  schema: string;
}> {
  const flightItems = items.filter(item => isFlightItem(item))
  if (!flightItems.length) return []

  const itineraryId = uuidV4()
  let segmentSequence = 1
  const journeysById = state.flights.journeysById

  return flightItems.flatMap((curr) => {
    // Create the itinerary context
    // For return flights, use the first flight's arrival (destination) instead of last flight's arrival (origin)
    const isReturnFlight = curr.fareType.toLowerCase().trim() === FlightsFareTypes.RETURN
    const arrivalAirportCode = isReturnFlight
      ? curr.flights[0]?.arrivalAirportCode
      : curr.flights.at(-1)?.arrivalAirportCode

    const itineraryContext = comluxgroup.createFlightItinerary_1_0_0({
      itineraryType: mapFlightFareType(curr.fareType),
      itineraryId: itineraryId,
      departureAirportCode: curr.flights[0]?.departingAirportCode,
      arrivalAirportCode,
      totalLayovers: sum(curr.flights, (flight) => flight.numberOfStops || 0),
      totalDuration: sum(curr.flights, flight => convertTimeObjectToMinutes(flight.duration)),
      layoverDuration: sum(curr.flights, flight => convertTimeObjectToMinutes(flight.layoverDuration))
    })

    // Create flight sector contexts for each flight in the item
    // Sector contexts provide detailed segment information and are optional
    const sectorContexts = curr.flights.flatMap((flight, flightIndex) => {
      // Try to get journey from the provided journeys array first
      let journey: App.JourneyV2 | undefined

      if (journeys && journeys.length > 0) {
        // If journeys are provided directly, use them
        // For flight items, the journeys array typically corresponds to the flights array
        journey = journeys[flightIndex]
      } else if (curr.searchId) {
        // Fall back to getting journey from state.flights.journeysById
        const journeyKey = getJourneyV2IdKey(flight.journeyId, curr.searchId, flight.fareFamily?.id)
        journey = journeysById[journeyKey] as App.JourneyV2 | undefined

        // If not found in journeysById, try to find it in searchV2Flights
        // This is important for non-logged-in users where journeysById might be empty
        if (!journey) {
          journey = findJourneyInSearchResults(state, curr.searchId, flight.journeyId)
        }
      }

      if (!journey) {
        return []
      }

      // Map each individual flight in the journey to a sector context
      return journey.flightGroup.flights.map((flightDetail) => comluxgroup.createFlightSector_1_0_0({
        segmentSequence: segmentSequence++,
        flightNumber: flightDetail.flightNumber,
        itineraryId,
        carrierCode: flightDetail.marketingAirline.code,
        carrierName: flightDetail.marketingAirline.name,
        provider: journey.provider,
        operatingCarrierCode: flightDetail.operatingAirline.code,
        operatingCarrierName: flightDetail.operatingAirline.name,
        departureAirportCode: flightDetail.departureAirport,
        departureCountry: getCode(flightDetail.departureCountry),
        arrivalAirportCode: flightDetail.arrivalAirport,
        arrivalCountry: getCode(flightDetail.arrivalCountry),
        departureTstamp: `${flightDetail.departingDate}T${flightDetail.departingTime}`,
        arrivalTstamp: `${flightDetail.arrivalDate}T${flightDetail.arrivalTime}`,
        duration: convertTimeObjectToMinutes(flightDetail.duration),
        fareFamily: journey.fareFamily?.description,
        fareClass: journey.fareBasisCode,
        cabinClass: mapFlightCabinClass(journey.cabin),
        aircraftType: flightDetail.aircraftType
      }))
    })

    return [itineraryContext, ...sectorContexts]
  })
}

export async function getOrderFlightContext(state: App.State, order: App.Order): Promise<Array<{
  data: {
    [x: string]: unknown;
  };
  schema: string;
}>> {
  const flightItems = order.flightItems
  if (!flightItems.length) return Promise.resolve([])

  const itineraryId = uuidV4()
  let segmentSequence = 1

  return Promise.all(flightItems.map(async(orderItem) => {
    let flightDetails = state?.orders?.flightDetails[orderItem?.id]?.flight
    if (!flightDetails) {
      flightDetails = await getFlightDetailsWithDedup({ orderId: order.id, itemId: orderItem.id })
    }

    return flightDetails.journeyFares.fares.flatMap((fare) => {
      // Create the itinerary context
      // For return flights, use the first flight's arrival (destination) instead of last flight's arrival (origin)
      const isReturnFlight = fare.fareType.toLowerCase().trim() === FlightsFareTypes.RETURN
      const arrivalAirportCode = isReturnFlight
        ? fare.flightGroup.flights[0].arrivalAirport
        : fare.flightGroup.flights.at(-1)?.arrivalAirport

      const itineraryContext = comluxgroup.createFlightItinerary_1_0_0({
        itineraryType: mapFlightFareType(fare.fareType),
        itineraryId: itineraryId,
        departureAirportCode: fare.flightGroup.flights[0].departureAirport,
        arrivalAirportCode,
        totalLayovers: fare.flightGroup.numberOfStops,
        totalDuration: convertTimeObjectToMinutes(fare.flightGroup.totalTravelDuration),
        layoverDuration: convertTimeObjectToMinutes(fare.flightGroup.totalStopOverTime)
      })

      // Create flight sector contexts for each flight in the group
      const sectorContexts = fare.flightGroup.flights.map((flight) => comluxgroup.createFlightSector_1_0_0({
        segmentSequence: segmentSequence++,
        flightNumber: flight.flightNumber,
        itineraryId,
        carrierCode: flight.marketingAirline.code,
        carrierName: flight.marketingAirline.name,
        provider: fare.provider,
        operatingCarrierCode: flight.operatingAirline.code,
        operatingCarrierName: flight.operatingAirline.name,
        departureAirportCode: flight.departureAirport,
        departureCountry: getCode(flight.departureCountry),
        arrivalAirportCode: flight.arrivalAirport,
        arrivalCountry: getCode(flight.arrivalCountry),
        departureTstamp: `${flight.departingDate}T${flight.departingTime}`,
        arrivalTstamp: `${flight.arrivalDate}T${flight.arrivalTime}`,
        duration: convertTimeObjectToMinutes(flight.duration),
        fareFamily: fare.fareFamily?.description,
        fareClass: fare.fareBasisCode,
        cabinClass: mapFlightCabinClass(fare.cabin),
        aircraftType: flight.aircraftType
      }))

      return [itineraryContext, ...sectorContexts]
    })
  })).then(itineraryContexts => itineraryContexts.flat().filter(
    (itineraryContext): itineraryContext is { data: { [x: string]: unknown }, schema: string } => itineraryContext !== undefined)
  )
}

export function getOfferFlightContexts(offer: TrackableProduct) {
  if (isJourneyV2(offer)) {
    const itineraryId = uuidV4()
    let segmentSequence = 1

    // For return flights, use the first flight's arrival (destination) instead of last flight's arrival (origin)
    const isReturnFlight = offer.fareType.toLowerCase().trim() === FlightsFareTypes.RETURN
    const arrivalAirportCode = isReturnFlight
      ? offer.flightGroup.flights[0].arrivalAirport
      : offer.flightGroup?.flights?.at(-1)?.arrivalAirport

    const flightItineraryContext = comluxgroup.createFlightItinerary_1_0_0({
      itineraryType: mapFlightFareType(offer.fareType),
      itineraryId,
      departureAirportCode: offer.flightGroup.flights[0].departureAirport,
      arrivalAirportCode,
      totalLayovers: offer.flightGroup.numberOfStops,
      totalDuration: convertTimeObjectToMinutes(offer.flightGroup.totalTravelDuration),
      layoverDuration: convertTimeObjectToMinutes(offer.flightGroup.totalStopOverTime)
    })

    const flightSectorContexs = offer.flightGroup.flights.map((flight) => comluxgroup.createFlightSector_1_0_0(
      {
        segmentSequence: segmentSequence++,
        flightNumber: flight.flightNumber,
        itineraryId,
        carrierCode: flight.marketingAirline.code,
        carrierName: flight.marketingAirline.name,
        provider: offer.provider,
        operatingCarrierCode: flight.operatingAirline.code,
        operatingCarrierName: flight.operatingAirline.name,
        departureAirportCode: flight.departureAirport,
        departureCountry: getCode(flight.departureCountry),
        arrivalAirportCode: flight.arrivalAirport,
        arrivalCountry: getCode(flight.arrivalCountry),
        departureTstamp: `${flight.departingDate}T${flight.departingTime}`,
        arrivalTstamp: `${flight.arrivalDate}T${flight.arrivalTime}`,
        duration: convertTimeObjectToMinutes(flight.duration),
        fareFamily: offer.fareFamily?.description,
        fareClass: offer.fareBasisCode,
        cabinClass: mapFlightCabinClass(offer.cabin),
        aircraftType: flight.aircraftType
      }))

    return [flightItineraryContext, ...flightSectorContexs]
  }

  return []
}

/**
 * Converts fare type string to contract schema format.
 *
 * Handles various fare type formats from different sources:
 * - FlightsFareTypes enum: 'oneWay', 'return', 'multiCity'
 * - API responses: 'OneWay', 'Return', 'MultiCity'
 * - Legacy formats: 'oneway', 'RETURN', etc.
 *
 * @param fareType - The fare type string in any case format
 * @returns Normalized fare type in snake_case format for event schemas
 */
function mapFlightFareType(fareType: string): 'one_way' | 'return' | 'multi_city' {
  const normalizedFareType = fareType.toLowerCase().trim()

  switch (normalizedFareType) {
    case 'oneway':
      return 'one_way'

    case 'return':
      return 'return'

    case 'multicity':
      return 'multi_city'

    default:
      // Fallback to 'return'
      return 'return'
  }
}

/**
 * Converts cabin class string to contract schema format.
 *
 * Handles various cabin class formats from different sources:
 * - FlightsClassTypes enum: 'economy', 'premium', 'business', 'first', 'luxrow'
 * - API responses: 'Economy', 'Premium', 'Business', 'First'
 * - Legacy formats: 'ECONOMY', etc.
 *
 * @param cabin - The cabin class string in any case format
 * @returns Normalized cabin class in snake_case format for event schemas
 */
function mapFlightCabinClass(cabin: string): 'economy' | 'premium_economy' | 'business' | 'first' {
  const normalizedCabin = cabin.toLowerCase().trim()

  switch (normalizedCabin) {
    case 'economy':
      return 'economy'

    case 'premium':
    case 'premium economy':
    case 'premium_economy':
      return 'premium_economy'

    case 'business':
      return 'business'

    case 'first':
      return 'first'

    default:
      // Fallback to 'economy'
      return 'economy'
  }
}
